/**
 * 에이전트의 상시 관찰 목록 — 패널 맨 위(2026-10-20, utils/agentObserver).
 *
 * 기계가 모은 일(쿼리 실패·느린/오래 도는 쿼리·오래된 미커밋·화면 오류·작업 실패 알림·감시 중지)을 보여 준다.
 * **AI 는 누를 때만** — 항목의 「분석 맡기기」 가 그 항목만 같은 대화로 보낸다(비용은 누른 만큼, 사용자 결정).
 * 접어 두면 한 줄(개수·가장 최근 것)만, 펼치면 목록. 펼친 동안 「새 관찰」 은 본 것으로 친다.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiEye, FiChevronDown, FiChevronRight, FiX, FiAlertCircle, FiAlertTriangle, FiInfo, FiCpu } from 'react-icons/fi';
import { useObservations, dismissObservation, clearObservations, markObservationsSeen } from '../../utils/agentObserver';
import './ObservationFeed.css';

const SEV_ICON = { error: <FiAlertCircle size={12} />, warn: <FiAlertTriangle size={12} />, info: <FiInfo size={12} /> };
const hhmm = (ms) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`; };

export default function ObservationFeed({ onAnalyze, busy, visible }) {
    const { t } = useTranslation();
    const { items, unseen } = useObservations();
    const [open, setOpen] = useState(false);
    // 펼쳐서 보고 있으면 본 것으로 — 패널이 접혀 있으면 보지 않은 것이다
    useEffect(() => { if (open && visible && unseen > 0) markObservationsSeen(); }, [open, visible, unseen]);

    const latest = items[0];
    return (
        <div className={`obs${open ? ' open' : ''}`}>
            <button type="button" className="obs-head" onClick={() => setOpen(v => !v)} aria-expanded={open} title={t('assistant:obs.hint')}>
                {open ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                <FiEye size={12} />
                <span className="obs-title">{t('assistant:obs.title')}</span>
                <span className="obs-count">{t('assistant:obs.count', { n: items.length })}</span>
                {unseen > 0 && <span className="obs-new">{t('assistant:obs.new', { n: unseen })}</span>}
                {!open && latest ? <span className={`obs-latest sev-${latest.severity}`}>{latest.title}</span> : null}
                {!open && !latest ? <span className="obs-latest">{t('assistant:obs.watching')}</span> : null}
            </button>
            {open && (
                <div className="obs-body">
                    {items.length === 0 ? (
                        <div className="obs-empty">{t('assistant:obs.empty')}</div>
                    ) : (
                        <>
                            <ul className="obs-list">
                                {items.map(it => (
                                    <li key={it.id} className={`obs-item sev-${it.severity}`}>
                                        <span className="obs-sev">{SEV_ICON[it.severity] || SEV_ICON.info}</span>
                                        <div className="obs-main">
                                            <div className="obs-line">
                                                <span className="obs-kind">{t(`assistant:obs.kind.${it.kind}`, { defaultValue: it.kind })}</span>
                                                <span className="obs-time">{hhmm(it.lastAt)}{it.count > 1 ? ` ×${it.count}` : ''}</span>
                                            </div>
                                            <div className="obs-text" title={it.detail || it.title}>{it.title}</div>
                                            {it.source ? <div className="obs-src">{it.source}</div> : null}
                                        </div>
                                        <div className="obs-actions">
                                            {onAnalyze && (
                                                <button type="button" className="obs-btn" disabled={busy} onClick={() => onAnalyze(it)} title={t('assistant:obs.analyzeHint')}>
                                                    <FiCpu size={11} /> {t('assistant:obs.analyze')}
                                                </button>
                                            )}
                                            <button type="button" className="obs-x" onClick={() => dismissObservation(it.id)} title={t('assistant:obs.dismiss')} aria-label={t('assistant:obs.dismiss')}>
                                                <FiX size={12} />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <div className="obs-foot">
                                <span>{t('assistant:obs.footNote')}</span>
                                <button type="button" className="obs-btn" onClick={clearObservations}>{t('assistant:obs.clearAll')}</button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

ObservationFeed.propTypes = {
    /** (item) — 그 항목을 에이전트에게 분석 맡긴다(같은 대화). 없으면 단추를 그리지 않는다 */
    onAnalyze: PropTypes.func,
    busy: PropTypes.bool,
    /** 패널이 보이는가 — 접힌 패널에서는 「본 것」 으로 치지 않는다 */
    visible: PropTypes.bool,
};
